import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage, formatCurrency, formatDate, formatDateShort } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ReceivePaymentDialog from "@/components/ReceivePaymentDialog";
import { toast } from "sonner";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
    Coins, Receipt, WarningOctagon, CalendarBlank, Plus, MagnifyingGlass,
    WhatsappLogo, Printer, DownloadSimple, Eye, TrendUp,
} from "@phosphor-icons/react";
import { buildWhatsAppUrl, paymentReminderMessage } from "@/lib/whatsapp";

const MODE_STYLES = {
    cash:          { bg: "#DCFCE7", fg: "#15803D", label: "Cash" },
    upi:           { bg: "#E0E7FF", fg: "#4338CA", label: "UPI" },
    bank_transfer: { bg: "#FEF3C7", fg: "#B45309", label: "Bank" },
    cheque:        { bg: "#FCE7F3", fg: "#9D174D", label: "Cheque" },
    other:         { bg: "#F3F4F6", fg: "#525252", label: "Other" },
};
const CHART_COLORS = ["#15803D", "#F59E0B", "#0A0A0A", "#DC2626", "#0EA5E9", "#8B5CF6"];

export default function Payments() {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [modeFilter, setModeFilter] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const [s, p] = await Promise.all([
                api.get("/dashboard/payment-stats"),
                api.get("/payments", { params: { limit: 500 } }),
            ]);
            setStats(s.data);
            setPayments(p.data);
        } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const filteredPayments = payments.filter((p) => {
        if (modeFilter !== "all" && p.payment_mode !== modeFilter) return false;
        if (!search) return true;
        const s = search.toLowerCase();
        return (
            (p.customer_name || "").toLowerCase().includes(s) ||
            (p.receipt_no || "").toLowerCase().includes(s) ||
            (p.invoice_no || "").toLowerCase().includes(s) ||
            (p.reference_no || "").toLowerCase().includes(s)
        );
    });

    const exportCsv = (kind) => {
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/payments?kind=${kind}`, "_blank");
    };

    const sendReminder = (cust) => {
        const msg = paymentReminderMessage({
            customerName: cust.name,
            pendingAmount: cust.pending,
        });
        window.open(buildWhatsAppUrl(cust.whatsapp || cust.mobile, msg), "_blank");
    };

    if (loading || !stats) {
        return <div className="text-sm text-neutral-500" data-testid="payments-loading">Loading payments...</div>;
    }

    return (
        <div className="space-y-6" data-testid="payments-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="eyebrow mb-2">Money in</div>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-display">Payment Management</h1>
                    <p className="text-sm text-neutral-500 mt-1.5">Receive, reconcile and remind — all in one place.</p>
                </div>
                <Button onClick={() => setDialogOpen(true)} className="text-white h-11" style={{ backgroundColor: "#15803D" }} data-testid="payments-receive-btn">
                    <Plus size={16} weight="bold" className="mr-1.5" /> Receive Payment
                </Button>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiTile label="Today's Collection" value={formatCurrency(stats.today_collection)} sub={`${stats.today_receipts} receipt${stats.today_receipts !== 1 ? "s" : ""}`} Icon={Coins} accent="#15803D" testId="pay-kpi-today" />
                <KpiTile label="Monthly Collection" value={formatCurrency(stats.month_collection)} sub={`${stats.month_collection_change >= 0 ? "+" : ""}${stats.month_collection_change}% vs last month`} Icon={TrendUp} accent="#15803D" testId="pay-kpi-month" />
                <KpiTile label="Pending Total" value={formatCurrency(stats.pending_total)} sub="Across all customers" Icon={Receipt} accent="#F59E0B" testId="pay-kpi-pending" />
                <KpiTile label="Overdue" value={`${stats.overdue_customers} customer${stats.overdue_customers !== 1 ? "s" : ""}`} sub={formatCurrency(stats.overdue_total)} Icon={WarningOctagon} accent="#DC2626" testId="pay-kpi-overdue" />
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-md p-4" data-testid="chart-collection">
                    <div className="eyebrow">Analytics</div>
                    <h3 className="text-base font-bold font-display mt-1 mb-2">Daily Collection (30d)</h3>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={stats.collection_graph}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} stroke="#9CA3AF" />
                            <YAxis tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                            <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e5e5e5", borderRadius: 6, fontSize: 12 }} formatter={(v) => formatCurrency(v)} />
                            <Bar dataKey="amount" fill="#15803D" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-white border border-neutral-200 rounded-md p-4" data-testid="chart-modes">
                    <div className="eyebrow">Analytics</div>
                    <h3 className="text-base font-bold font-display mt-1 mb-2">Mode Breakdown (M)</h3>
                    {stats.mode_breakdown.length === 0 ? (
                        <div className="h-[220px] flex items-center justify-center text-sm text-neutral-400">No payments this month</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie data={stats.mode_breakdown} dataKey="amount" nameKey="mode" cx="50%" cy="50%" innerRadius={45} outerRadius={80}>
                                    {stats.mode_breakdown.map((_, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
                                </Pie>
                                <Tooltip formatter={(v) => formatCurrency(v)} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            <Tabs defaultValue="history">
                <TabsList>
                    <TabsTrigger value="history" data-testid="pay-tab-history">Payment History</TabsTrigger>
                    <TabsTrigger value="pending" data-testid="pay-tab-pending">Pending & Overdue</TabsTrigger>
                    <TabsTrigger value="reports" data-testid="pay-tab-reports">Reports</TabsTrigger>
                </TabsList>

                <TabsContent value="history" className="mt-4 space-y-4">
                    <div className="flex flex-col md:flex-row gap-3">
                        <div className="relative flex-1">
                            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                            <Input placeholder="Search receipt, customer, invoice, reference..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-11" data-testid="payments-search" />
                        </div>
                        <Select value={modeFilter} onValueChange={setModeFilter}>
                            <SelectTrigger className="w-full md:w-56 h-11" data-testid="payments-mode-filter"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Modes</SelectItem>
                                <SelectItem value="cash">Cash</SelectItem>
                                <SelectItem value="upi">UPI</SelectItem>
                                <SelectItem value="bank_transfer">Bank</SelectItem>
                                <SelectItem value="cheque">Cheque</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="bg-white border border-neutral-200 rounded-md overflow-hidden" data-testid="payments-table">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                                    <tr>
                                        <th className="text-left px-5 py-3 font-semibold">Receipt</th>
                                        <th className="text-left px-5 py-3 font-semibold">Date</th>
                                        <th className="text-left px-5 py-3 font-semibold">Customer</th>
                                        <th className="text-left px-5 py-3 font-semibold">Invoice</th>
                                        <th className="text-left px-5 py-3 font-semibold">Mode</th>
                                        <th className="text-left px-5 py-3 font-semibold">Reference</th>
                                        <th className="text-left px-5 py-3 font-semibold">Received By</th>
                                        <th className="text-right px-5 py-3 font-semibold">Amount</th>
                                        <th className="px-5 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredPayments.length === 0 && (
                                        <tr><td colSpan={9} className="text-center py-10 text-neutral-400">No payments recorded yet.</td></tr>
                                    )}
                                    {filteredPayments.map((p) => {
                                        const s = MODE_STYLES[p.payment_mode] || MODE_STYLES.other;
                                        return (
                                            <tr key={p.id} className="data-row border-t border-neutral-100" data-testid={`payment-row-${p.receipt_no}`}>
                                                <td className="px-5 py-3 font-mono text-xs font-semibold">{p.receipt_no}</td>
                                                <td className="px-5 py-3 text-neutral-500 text-xs whitespace-nowrap">{formatDate(p.payment_date)}</td>
                                                <td className="px-5 py-3 font-medium">{p.customer_name}</td>
                                                <td className="px-5 py-3 font-mono text-xs text-neutral-600">{p.invoice_no || "—"}</td>
                                                <td className="px-5 py-3">
                                                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded" style={{ background: s.bg, color: s.fg }}>{s.label}</span>
                                                </td>
                                                <td className="px-5 py-3 text-xs text-neutral-500 max-w-[160px] truncate">{p.reference_no || "—"}</td>
                                                <td className="px-5 py-3 text-xs text-neutral-500">{p.received_by_name}</td>
                                                <td className="px-5 py-3 text-right font-bold tabular font-display" style={{ color: "#15803D" }}>{formatCurrency(p.amount)}</td>
                                                <td className="px-5 py-3 text-right whitespace-nowrap">
                                                    <button onClick={() => navigate(`/receipt/${p.id}`)} className="text-neutral-500 hover:text-neutral-900 mx-1" title="Open receipt" data-testid={`payment-open-${p.receipt_no}`}>
                                                        <Eye size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="pending" className="mt-4">
                    <div className="bg-white border border-neutral-200 rounded-md overflow-hidden" data-testid="pending-table">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                                    <tr>
                                        <th className="text-left px-5 py-3 font-semibold">Customer</th>
                                        <th className="text-left px-5 py-3 font-semibold">Mobile</th>
                                        <th className="text-right px-5 py-3 font-semibold">Pending</th>
                                        <th className="text-right px-5 py-3 font-semibold">Overdue</th>
                                        <th className="text-right px-5 py-3 font-semibold">Credit Limit</th>
                                        <th className="text-right px-5 py-3 font-semibold">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.pending_customers.length === 0 && (
                                        <tr><td colSpan={6} className="text-center py-10 text-neutral-400">All caught up — no pending payments.</td></tr>
                                    )}
                                    {stats.pending_customers.map((c) => (
                                        <tr key={c.id} className="data-row border-t border-neutral-100" data-testid={`pending-row-${c.name}`}>
                                            <td className="px-5 py-3 font-semibold">{c.name}</td>
                                            <td className="px-5 py-3 text-neutral-600">{c.mobile || "—"}</td>
                                            <td className="px-5 py-3 text-right tabular font-bold" style={{ color: "#F59E0B" }}>{formatCurrency(c.pending)}</td>
                                            <td className="px-5 py-3 text-right tabular font-bold" style={{ color: c.overdue > 0 ? "#DC2626" : "#9CA3AF" }}>{formatCurrency(c.overdue)}</td>
                                            <td className="px-5 py-3 text-right tabular text-neutral-500">{formatCurrency(c.credit_limit)}</td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                <button onClick={() => sendReminder(c)}
                                                        className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded"
                                                        style={{ background: "#DCFCE7", color: "#15803D" }}
                                                        data-testid={`pending-remind-${c.name}`}>
                                                    <WhatsappLogo size={12} weight="fill" /> Remind
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="reports" className="mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[
                            { kind: "paid", label: "Paid Report", desc: "All received payments" },
                            { kind: "pending", label: "Pending Report", desc: "Outstanding by customer" },
                            { kind: "outstanding", label: "Outstanding Report", desc: "Same as pending, formatted" },
                            { kind: "cash", label: "Cash Report", desc: "Cash payments only" },
                            { kind: "bank", label: "Bank Report", desc: "Bank transfer payments" },
                            { kind: "daily", label: "Daily Collection", desc: "30-day daily breakdown" },
                            { kind: "monthly", label: "Monthly Collection", desc: "12-month totals" },
                        ].map((r) => (
                            <button key={r.kind} onClick={() => exportCsv(r.kind)}
                                    className="text-left bg-white border border-neutral-200 rounded-md p-4 hover:border-neutral-400 transition-colors"
                                    data-testid={`report-${r.kind}`}>
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="font-bold font-display">{r.label}</div>
                                        <div className="text-xs text-neutral-500 mt-1">{r.desc}</div>
                                    </div>
                                    <DownloadSimple size={18} className="text-neutral-400" />
                                </div>
                                <div className="mt-3 text-xs font-semibold" style={{ color: "#15803D" }}>Download CSV →</div>
                            </button>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>

            <ReceivePaymentDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={(p) => { load(); navigate(`/receipt/${p.id}`); }} />
        </div>
    );
}

function KpiTile({ label, value, sub, Icon, accent, testId }) {
    return (
        <div className="kpi-card bg-white border border-neutral-200 rounded-md p-4" data-testid={testId}>
            <div className="flex items-start justify-between mb-3">
                <div className="eyebrow">{label}</div>
                <div className="w-9 h-9 rounded flex items-center justify-center" style={{ background: accent + "14", color: accent }}>
                    <Icon size={18} weight="bold" />
                </div>
            </div>
            <div className="text-2xl font-extrabold font-display tabular tracking-tight" style={{ color: "#0A0A0A" }}>{value}</div>
            <div className="text-xs text-neutral-500 mt-1">{sub}</div>
        </div>
    );
}
